import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Alert, Box, Button, Card, CardContent, LinearProgress, Stack, TextField, Typography } from '@mui/material'

import ConfirmDialog from '../../../../../components/ConfirmDialog/ConfirmDialog'
import { ConfirmDialogData } from '../../../../../types/ConfirmDialogData'
import {
  useGetInternalSettingQuery,
  useRefreshAnalysesTagsMutation,
  useRefreshAnalysesTopFeaturesMutation,
  useSaveServiceVersionInfoMutation,
} from '../../../../api/settings'
import { QueryStateBoundary } from '../../../../components/QueryStateBoundary/QueryStateBoundary'
import { useMutationFeedback } from '../../../../hooks/useMutationFeedback'
import { ServiceVersionInfoData } from '../../types/types'

type SystemAction = 'versionInfo' | 'topFeatures' | 'tags'

const refreshActions: { action: Exclude<SystemAction, 'versionInfo'>; key: string }[] = [
  { action: 'topFeatures', key: 'top_features' },
  { action: 'tags', key: 'tags' },
]

export const SystemActionsTab: FC = () => {
  const { t } = useTranslation()
  const { run, notifySuccess, notifyError } = useMutationFeedback()
  const settingQuery = useGetInternalSettingQuery('serviceVersionInfo')
  const [saveServiceVersionInfo, { isLoading: isSavingVersion }] = useSaveServiceVersionInfoMutation()
  const [refreshTopFeatures] = useRefreshAnalysesTopFeaturesMutation()
  const [refreshTags] = useRefreshAnalysesTagsMutation()
  const [editedVersion, setEditedVersion] = useState<string>()
  const [runningAction, setRunningAction] = useState<SystemAction>()
  const [confirmDialogData, setConfirmDialogData] = useState<ConfirmDialogData<SystemAction>>()

  const storedData = (settingQuery.data?.data ?? {}) as ServiceVersionInfoData
  const storedVersion = storedData.versionNumber ?? ''
  const versionNumber = editedVersion ?? storedVersion
  const trimmedVersion = versionNumber.trim()
  const versionError = !trimmedVersion ? t('internal-service-management:settings_version_required') : undefined
  const canSaveVersion = !versionError && trimmedVersion !== storedVersion && !runningAction

  const confirm = (action: SystemAction) => {
    if (action === 'versionInfo') {
      setConfirmDialogData({
        title: t('internal-service-management:settings_version_save'),
        confirmText: (
          <Stack spacing={2}>
            <Typography>{t('internal-service-management:settings_version_confirm', { from: storedVersion, to: trimmedVersion })}</Typography>
            <Alert severity="warning">{t('internal-service-management:settings_version_confirm_warning')}</Alert>
          </Stack>
        ),
        actionText: t('internal-service-management:settings_version_save'),
        data: action,
      })
      return
    }
    const key = refreshActions.find((candidate) => candidate.action === action)?.key
    setConfirmDialogData({
      title: t(`internal-service-management:settings_refresh_${key}`),
      confirmText: (
        <Stack spacing={2}>
          <Typography>{t(`internal-service-management:settings_refresh_${key}_confirm`)}</Typography>
          <Alert severity="info">{t('internal-service-management:settings_refresh_duration')}</Alert>
        </Stack>
      ),
      actionText: t(`internal-service-management:settings_refresh_${key}`),
      data: action,
    })
  }

  const runVersionInfoSave = async () => {
    const saved = await run(saveServiceVersionInfo({ ...storedData, versionNumber: trimmedVersion }).unwrap(), {
      error: t('internal-service-management:settings_version_save_failed'),
    })
    if (!saved.ok) return
    if (saved.result?.data?.versionNumber !== trimmedVersion) {
      notifyError(t('internal-service-management:settings_version_not_stored'))
      return
    }
    setEditedVersion(undefined)
    notifySuccess(t('internal-service-management:settings_version_saved', { version: trimmedVersion }))
  }

  const runRefresh = async (action: Exclude<SystemAction, 'versionInfo'>) => {
    const key = refreshActions.find((candidate) => candidate.action === action)?.key
    setRunningAction(action)
    await run((action === 'topFeatures' ? refreshTopFeatures() : refreshTags()).unwrap(), {
      success: t(`internal-service-management:settings_refresh_${key}_done`),
      error: t(`internal-service-management:settings_refresh_${key}_failed`),
    })
    setRunningAction(undefined)
  }

  const handleConfirm = (confirmed?: ConfirmDialogData<SystemAction>) => {
    setConfirmDialogData(undefined)
    const action = confirmed?.data
    if (!action) return
    if (action === 'versionInfo') runVersionInfoSave()
    else runRefresh(action)
  }

  return (
    <Stack spacing={3}>
      <Typography color="text.secondary">{t('internal-service-management:settings_system_description')}</Typography>

      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" mb={1}>
            {t('internal-service-management:settings_version_title')}
          </Typography>
          <Typography color="text.secondary" mb={2}>
            {t('internal-service-management:settings_version_description')}
          </Typography>
          <QueryStateBoundary
            isLoading={settingQuery.isLoading}
            isError={settingQuery.isError}
            errorText={t('internal-service-management:settings_load_failed')}
            skeletonHeight={56}
          >
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <TextField
                size="small"
                label={t('internal-service-management:settings_version_number')}
                value={versionNumber}
                onChange={(event) => setEditedVersion(event.target.value)}
                error={!!versionError}
                helperText={versionError ?? t('internal-service-management:settings_version_number_hint')}
                disabled={isSavingVersion}
                sx={{ minWidth: 280 }}
              />
              <Button variant="contained" onClick={() => confirm('versionInfo')} disabled={!canSaveVersion || isSavingVersion}>
                {t('internal-service-management:settings_version_save')}
              </Button>
            </Stack>
          </QueryStateBoundary>
        </CardContent>
      </Card>

      {runningAction && runningAction !== 'versionInfo' && (
        <Alert severity="info" icon={false}>
          <Typography mb={1}>
            {t(`internal-service-management:settings_refresh_${refreshActions.find((candidate) => candidate.action === runningAction)?.key}_running`)}
          </Typography>
          <LinearProgress />
        </Alert>
      )}

      <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: 'repeat(2, 1fr)' }} gap={3}>
        {refreshActions.map(({ action, key }) => (
          <Card key={action}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" mb={1}>
                {t(`internal-service-management:settings_refresh_${key}_title`)}
              </Typography>
              <Typography color="text.secondary" mb={2}>
                {t(`internal-service-management:settings_refresh_${key}_description`)}
              </Typography>
              <Button variant="outlined" onClick={() => confirm(action)} disabled={!!runningAction}>
                {t(`internal-service-management:settings_refresh_${key}`)}
              </Button>
            </CardContent>
          </Card>
        ))}
      </Box>

      <ConfirmDialog open={!!confirmDialogData} confirmDialogData={confirmDialogData} onClose={handleConfirm} />
    </Stack>
  )
}
